import { useCallback, useRef } from 'react'
import {
  TurnRefused, refusalCode, sendDraft, sendNew, streamTurn,
} from '@/services/helpFeedback/appFeedbackApi'
import { useAuthStore } from '@/stores/authStore'
import { useHelpFeedbackStore } from '@/stores/helpFeedbackStore'
import {
  firstWords, guidanceBody, openOffer, sendBody, startConversation, turnBody, type Card, type Conversation,
} from './conversation'
import { CHIP_WORDS, VIEW_AS_LINE, refusalReason, type ChipKey } from './helpFeedbackText'

const store = () => useHelpFeedbackStore.getState()

/** The person's own words, joined: what the plain report starts from when the assistant is not set up. */
function wordsOf(c: Conversation): string {
  return c.turns.flatMap((t) => (t.type === 'person' && !t.chip ? [t.text] : [])).join('\n\n')
}

/**
 * Runs one turn. The callbacks dispatch through the store, never through component state, so a turn
 * that is still streaming when the panel closes lands all the same.
 */
async function runTurn(message: string, spoken: boolean, oneTap: boolean): Promise<void> {
  const c = store().conversation
  if (!c) return
  try {
    await streamTurn(turnBody(c, message, spoken), {
      onDraft: (feedbackId) => store().dispatch({ type: 'draft', feedbackId }),
      onToken: (token) => store().dispatch({ type: 'token', token }),
      onTurn: (turn) => store().dispatch({ type: 'turn', turn, oneTap }),
      onError: () => store().dispatch({ type: 'turnFailed' }),
    })
  } catch (err) {
    const code = err instanceof TurnRefused ? refusalCode(err) : null
    if (code === 'assistant_unavailable') {
      // Nothing was written: the plain report takes over with the words so far.
      const now = store().conversation ?? c
      store().setPlainWords(wordsOf(now))
      store().start({ ...startConversation(now.opener, now.snapshot, now.startedAt), removed: now.removed })
      store().setStatus('unavailable')
    } else if (code === 'view_as_forbidden') {
      store().dispatch({ type: 'say', text: VIEW_AS_LINE })
    } else {
      store().dispatch({ type: 'turnFailed' })
    }
  }
}

/** A refusal's `{reason}` for a failed send. */
function failedReason(err: unknown): string {
  return refusalReason(refusalCode(err))
}

/** The Ask tab's actions: the person's words, chips, the card's send and the answer's two buttons. */
export function useAsk() {
  const viewAs = useAuthStore((s) => !!s.impersonation)
  // What Try again repeats: the card's send or the guidance note.
  const retry = useRef<(() => void) | null>(null)

  /** The person's words as a turn. The composer holds words typed during a turn until it ends. */
  const say = useCallback((text: string, spoken = false) => {
    const words = text.trim()
    const c = store().conversation
    if (!words || !c || viewAs || c.waiting !== 'none') return false
    store().dispatch({ type: 'personSaid', text: words, spoken })
    void runTurn(words, spoken, !viewAs)
    return true
  }, [viewAs])

  const chip = useCallback((key: ChipKey) => {
    if (viewAs) return
    store().dispatch({ type: 'chip', chip: key })
    if (key === 'explain') void runTurn(CHIP_WORDS.explain, false, !viewAs)
  }, [viewAs])

  const send = useCallback(async (card?: Card) => {
    const c = store().conversation
    if (!c || c.waiting !== 'none' || viewAs) return
    retry.current = () => void send(card)
    const s = card ? sendBody(c, card) : sendBody(c)
    store().dispatch({ type: 'sending' })
    try {
      const report = s.draft === null ? await sendNew(s.body) : await sendDraft(s.draft, s.body)
      store().dispatch({ type: 'sent', report })
    } catch (err) {
      store().dispatch({ type: 'sendFailed', reason: failedReason(err) })
    }
  }, [viewAs])

  const sendGuidance = useCallback(async (c: Conversation) => {
    retry.current = () => void sendGuidance(c)
    const body = guidanceBody(c)
    try {
      if (c.feedbackId) await sendDraft(c.feedbackId, body)
      else await sendNew({ ...body, message: firstWords(c) || body.summary || '…', spoken: false })
    } catch (err) {
      store().dispatch({ type: 'sendFailed', reason: failedReason(err) })
    }
  }, [])

  const resolved = useCallback(() => {
    if (viewAs) return
    store().dispatch({ type: 'guidanceResolved' })
    const c = store().conversation
    if (c) void sendGuidance(c)
  }, [viewAs, sendGuidance])

  /** The not-found offer's Send to the team: the question as the help could not answer it (SA-1314). */
  const sendQuestion = useCallback(() => {
    const c = store().conversation
    const card = c && openOffer(c)
    if (card) void send(card)
  }, [send])

  const askAnotherWay = useCallback(() => store().dispatch({ type: 'askAnotherWay' }), [])

  const notQuite = useCallback(() => store().dispatch({ type: 'notQuite' }), [])

  const tryAgain = useCallback(() => retry.current?.(), [])

  return { viewAs, say, chip, send, sendQuestion, askAnotherWay, resolved, notQuite, tryAgain }
}
